/**
 * Profile / friends naming helpers.
 *
 * - `player_name` is the primary handle (games, leaderboards, and the main label on profile/friends).
 * - `display_name` is an optional real/friendly name shown secondarily to the owner and friends.
 */

type NamePerson = {
  display_name?: string | null;
  player_name?: string | null;
  playerName?: string | null;
  username?: string | null;
} | null | undefined;

/** Primary handle: player_name → username → "Player". */
export function playerHandleName(person: NamePerson): string {
  if (!person) return "Player";
  const player =
    (typeof person.player_name === "string" && person.player_name.trim()) ||
    (typeof person.playerName === "string" && person.playerName.trim()) ||
    "";
  if (player) return player;
  const username = typeof person.username === "string" ? person.username.trim() : "";
  return username || "Player";
}

/**
 * Optional real/friendly name when set. Returns null when empty or identical to the handle
 * (case-insensitive) so we don't duplicate the primary label.
 */
export function optionalDisplayName(person: NamePerson): string | null {
  if (!person) return null;
  const display = typeof person.display_name === "string" ? person.display_name.trim() : "";
  if (!display) return null;
  const handle = playerHandleName(person);
  if (display.toLowerCase() === handle.toLowerCase()) return null;
  return display;
}

/** True when the viewer may see the subject's `display_name` (owner or accepted friends). */
export function canSeeDisplayName(opts: {
  isOwnProfile?: boolean;
  friendshipStatus?: string | null;
}): boolean {
  if (opts.isOwnProfile) return true;
  return opts.friendshipStatus === "friends";
}

/**
 * @deprecated Prefer {@link playerHandleName} + {@link optionalDisplayName}.
 * Kept for call sites that need a single string (still returns the primary handle).
 */
export function friendFacingName(person: NamePerson): string {
  return playerHandleName(person);
}
